import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Collapse, Drawer, Select, Space, Spin } from 'antd';
import type { TripHistogramType } from '../../../infrastructure/dataApi/client';
import { useAggregationConfig } from '../../../hooks/SimViewer/useAggregationConfig';
import {
  useDataFilter,
  type RangeFilterType,
} from '../../../hooks/SimViewer/useDataFilter';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useTripHistogramData } from '../../../hooks/data/useTripHistogramData';
import { downloadAsCsv } from '../../../utils/file';
import { isSameRange } from '../../../utils/range';
import { formatAny } from '../../../utils/format';
import { toPascal } from '../../../utils/case';
import { includes } from '../../../utils/array';
import { HistogramChart } from '../Chart/HistogramChart';
import { FilterIcon } from '../../FilterIcon';
import { MapMarkedIconButton } from '../../MapMarkedIconButton';
import { ActionButtons } from './ActionButtons';
import { HistogramComparison } from './HistogramComparison';

type HistogramPanelType = Exclude<
  TripHistogramType,
  'passage_time' | 'staying_population'
>;

type Props = {
  type: HistogramPanelType;
  icon: ReactNode;
  label: string;
  extra?: ReactNode;
};

const RANGE_TYPES = {
  traveled_time: 'duration',
  activity_time: 'duration',
  traveled_distance: 'distance',
  dep_time: 'time',
  arr_time: 'time',
  passage_time: 'time',
  staying_population: 'time',
} as const satisfies Record<
  TripHistogramType,
  'time' | 'duration' | 'distance'
>;

const VALUE_TYPES = {
  traveled_time: 'duration',
  activity_time: 'duration',
  traveled_distance: 'distance',
  dep_time: 'time',
  arr_time: 'time',
  passage_time: 'time',
  staying_population: 'count',
} as const satisfies Record<
  TripHistogramType,
  'time' | 'duration' | 'distance' | 'count'
>;

const FILTER_KEYS = {
  traveled_time: 'traveledTime',
  traveled_distance: 'traveledDistance',
  activity_time: 'activityTime',
  dep_time: 'depTime',
  arr_time: 'arrTime',
} as const satisfies Record<HistogramPanelType, RangeFilterType>;

const HistogramPanel = ({
  type,
  icon,
  label,
  extra,
  ...props
}: Props): ReactNode => {
  const { dataFilter, setStopBy } = useDataFilter();
  const { aggregationConfig, startAggregation, clearAggregation } =
    useAggregationConfig();

  return (
    <Collapse.Panel
      key={type}
      header={
        <Space>
          {icon}
          {label}
          {extra}
          {dataFilter[FILTER_KEYS[type]] && <FilterIcon />}
        </Space>
      }
      extra={
        includes(
          ['traveled_time', 'traveled_distance', 'activity_time'],
          type
        ) && (
          <MapMarkedIconButton
            active={aggregationConfig?.target === type}
            onClick={(): void => {
              if (aggregationConfig?.target === type) {
                clearAggregation();
              } else {
                startAggregation(type);
              }
              setStopBy(null);
            }}
          />
        )
      }
      {...props}
    >
      <HistogramPanelContent type={type} label={label} />
    </Collapse.Panel>
  );
};

const HistogramPanelContent = ({
  type,
  label,
}: Pick<Props, 'type' | 'label'>): ReactNode => {
  const { t } = useTranslation();
  const { dataFilter, setRangeFilter } = useDataFilter();
  const { aggregationConfig, changeAggregationSubTarget } =
    useAggregationConfig();
  const { currentDataMeta } = useMetaData();
  const { isLoading, mean, median, items } = useTripHistogramData(type);

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  return (
    <Spin spinning={isLoading}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space>
          <Text>
            {t('mean')}：
            {mean !== null ? formatAny(VALUE_TYPES[type], mean) : '-'}
          </Text>
          <Text>
            {t('median')}：
            {median !== null ? formatAny(VALUE_TYPES[type], median) : '-'}
          </Text>
        </Space>
        <HistogramChart
          data={items}
          rangeType={RANGE_TYPES[type]}
          noDataMessage={t('message.noTripFound')}
          filterRange={dataFilter[FILTER_KEYS[type]]}
          onFilterRangeChange={(
            newFilterRange: [number, number] | null
          ): void => {
            if (!isSameRange(newFilterRange, dataFilter[FILTER_KEYS[type]])) {
              setRangeFilter(FILTER_KEYS[type], newFilterRange);
            }
          }}
        />
        {aggregationConfig?.target === type && (
          <Space>
            <Select
              size="small"
              value={aggregationConfig.subTarget}
              style={{ width: 120 }}
              onChange={changeAggregationSubTarget}
            >
              <Select.Option value="end">{t('tripEnd')}</Select.Option>
              <Select.Option value="start">{t('tripStart')}</Select.Option>
              <Select.Option value="home">{t('home')}</Select.Option>
              <Select.Option value="work">{t('work')}</Select.Option>
            </Select>
            <Text>{t('aggregatedBySubTarget')}</Text>
          </Space>
        )}
        <ActionButtons
          onOpenComparison={(): void => {
            setIsDrawerOpen(true);
          }}
          onExportCsv={(withBom: boolean): void => {
            downloadAsCsv(
              [
                [
                  label,
                  `${t('agentCount')}${t('unit.agent') ? ` [${t('unit.agent')}]` : ''}`,
                ],
                ...items.map((row): (string | number)[] => {
                  return [
                    `${formatAny(RANGE_TYPES[type], row.range[0])}~${formatAny(RANGE_TYPES[type], row.range[1])}`,
                    row.value,
                  ];
                }),
              ],
              `${currentDataMeta?.name ?? ''}_${toPascal(label)}.csv`,
              { withBom: withBom }
            );
          }}
          disabled={isLoading}
        />
        <Drawer
          title={t('barChartTitle', { target: label })}
          placement="right"
          width="80%"
          destroyOnClose
          open={isDrawerOpen}
          onClose={(): void => {
            setIsDrawerOpen(false);
          }}
        >
          <HistogramComparison
            type={type}
            noDataMessage={t('message.noTripFound')}
          />
        </Drawer>
      </Space>
    </Spin>
  );
};

const Text = styled.div`
  font-size: 12px;
`;

export { HistogramPanel };
